import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { AuthContext } from "../../../context/AuthContext/AuthContext";
import {
  fetchUserAlerts,
  fetchUnsignedNotes,
  ALERT_CATEGORIES,
} from "./services/alertsMockService";
import { PENDING_NOTES_EVENT } from "./services/pendingNotesStore";
import { PHYSICIAN_QUERIES_EVENT } from "./services/physicianQueriesStore";
import { CARE_GAP_REVIEWS_EVENT } from "./services/careGapReviewsStore";
import { ALERTS_ENABLED } from "./constants";

/**
 * Contexto global de alertas/notificaciones de la app autenticada.
 *
 * Se monta en el Layout (solo aplica a la app con sesión). Expone:
 *  - alerts / unreadCount
 *  - drawerOpen + openDrawer/closeDrawer (panel lateral derecho)
 *  - markAsRead / markAllAsRead (persistido en sessionStorage)
 *  - loginAlert (snackbar de login: N notas sin firmar en AdvancedMD)
 *
 * Los datos vienen de `services/alertsMockService.js` (MOCK — ver JSDoc ahí
 * para el plan de reemplazo por endpoints reales).
 */
const AlertsContext = createContext(null);

/**
 * Hook de consumo. Devuelve `null` si no hay provider montado (permite que
 * componentes como la campana del AppBar degraden sin romper si se
 * renderizan fuera del Layout).
 */
export const useAlerts = () => useContext(AlertsContext);

/** Claves de sessionStorage, namespaced por empleado para no mezclar sesiones. */
const readStateKey = (employeeId) => `mcm_alerts_read_${employeeId}`;
const loginAlertShownKey = (employeeId) => `mcm_login_alert_shown_${employeeId}`;

/** Lee los alertId marcados como leídos en esta sesión de navegador. */
const loadReadIds = (employeeId) => {
  try {
    const raw = sessionStorage.getItem(readStateKey(employeeId));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

/** Persiste los alertId leídos (sobrevive navegación, muere al cerrar la tab). */
const saveReadIds = (employeeId, ids) => {
  try {
    sessionStorage.setItem(readStateKey(employeeId), JSON.stringify(ids));
  } catch {
    // sessionStorage lleno o bloqueado: el estado leído queda solo en memoria.
  }
};

export const AlertsProvider = ({ children }) => {
  const { user } = useContext(AuthContext);
  const employeeId = user?.employeeId ?? null;

  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Tick de recarga: se incrementa cuando el store local de notas pendientes
  // de firma cambia (una nota terminada en CDCI aparece en el panel al
  // instante, sin recargar la página).
  const [reloadTick, setReloadTick] = useState(0);
  useEffect(() => {
    if (!ALERTS_ENABLED) return undefined;
    const onChange = () => setReloadTick((t) => t + 1);
    window.addEventListener(PENDING_NOTES_EVENT, onChange);
    window.addEventListener(PHYSICIAN_QUERIES_EVENT, onChange);
    window.addEventListener(CARE_GAP_REVIEWS_EVENT, onChange);
    return () => {
      window.removeEventListener(PENDING_NOTES_EVENT, onChange);
      window.removeEventListener(PHYSICIAN_QUERIES_EVENT, onChange);
      window.removeEventListener(CARE_GAP_REVIEWS_EVENT, onChange);
    };
  }, []);

  // Dialog prominente de login: { open, count, notes } | null. `notes` es la
  // lista de notas sin firmar (noteType, patientName, serviceDate…) que el
  // dialog muestra con los días transcurridos sin firma.
  const [loginAlert, setLoginAlert] = useState(null);

  /**
   * Carga inicial de alertas + alerta de login (una vez por sesión).
   * Se dispara cuando aparece un employeeId autenticado.
   */
  useEffect(() => {
    // DEV-ONLY: en producción el kit no carga nada. Cortar acá evita el fetch
    // del mock service y las escrituras en sessionStorage, no solo el render.
    if (!ALERTS_ENABLED) return;

    if (!employeeId) {
      // Logout: limpiar estado en memoria.
      setAlerts([]);
      setLoginAlert(null);
      setDrawerOpen(false);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const [fetched, unsigned] = await Promise.all([
          fetchUserAlerts(employeeId),
          fetchUnsignedNotes(employeeId),
        ]);
        if (cancelled) return;

        // Aplicar estado leído persistido en sessionStorage sobre el fetch.
        const readIds = loadReadIds(employeeId);
        let merged = fetched.map((a) =>
          readIds.includes(a.alertId) ? { ...a, read: true } : a
        );

        // Alerta de login simulada: una sola vez por sesión de navegador.
        const alreadyShown = sessionStorage.getItem(
          loginAlertShownKey(employeeId)
        );
        const unsignedNotes = unsigned?.notes ?? [];
        const count = unsigned?.total ?? unsignedNotes.length;

        if (count > 0) {
          // La alerta de notas sin firmar también vive en el panel.
          const unsignedAlert = {
            alertId: "cn-unsigned-login",
            category: ALERT_CATEGORIES.CLINICAL_NOTES,
            title: "Unsigned clinical notes in AdvancedMD",
            description: `You have ${count} unsigned clinical note${
              count === 1 ? "" : "s"
            } in AdvancedMD. Please review and sign them.`,
            createdAt: new Date().toISOString(),
            read: readIds.includes("cn-unsigned-login"),
          };
          merged = [unsignedAlert, ...merged];

          if (!alreadyShown) {
            setLoginAlert({ open: true, count, notes: unsignedNotes });
            try {
              sessionStorage.setItem(loginAlertShownKey(employeeId), "1");
            } catch {
              // Ignorar: peor caso, se repite el snackbar en el próximo mount.
            }
          }
        }

        setAlerts(merged);
      } catch (err) {
        console.warn("Could not load user alerts:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [employeeId, reloadTick]);

  const openDrawer = useCallback(() => setDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  /** Cierra el snackbar de login (la alerta ya quedó en el panel). */
  const dismissLoginAlert = useCallback(() => {
    setLoginAlert((prev) => (prev ? { ...prev, open: false } : prev));
  }, []);

  /** Marca una alerta como leída y persiste en sessionStorage. */
  const markAsRead = useCallback(
    (alertId) => {
      setAlerts((prev) => {
        const next = prev.map((a) =>
          a.alertId === alertId ? { ...a, read: true } : a
        );
        if (employeeId) {
          saveReadIds(
            employeeId,
            next.filter((a) => a.read).map((a) => a.alertId)
          );
        }
        return next;
      });
    },
    [employeeId]
  );

  /** Marca todas las alertas como leídas y persiste en sessionStorage. */
  const markAllAsRead = useCallback(() => {
    setAlerts((prev) => {
      const next = prev.map((a) => (a.read ? a : { ...a, read: true }));
      if (employeeId) {
        saveReadIds(employeeId, next.map((a) => a.alertId));
      }
      return next;
    });
  }, [employeeId]);

  const unreadCount = useMemo(
    () => alerts.filter((a) => !a.read).length,
    [alerts]
  );

  const value = useMemo(
    () => ({
      alerts,
      loading,
      unreadCount,
      drawerOpen,
      openDrawer,
      closeDrawer,
      markAsRead,
      markAllAsRead,
      loginAlert,
      dismissLoginAlert,
    }),
    [
      alerts,
      loading,
      unreadCount,
      drawerOpen,
      openDrawer,
      closeDrawer,
      markAsRead,
      markAllAsRead,
      loginAlert,
      dismissLoginAlert,
    ]
  );

  return (
    <AlertsContext.Provider value={value}>{children}</AlertsContext.Provider>
  );
};
